import React, { useEffect, useState } from "react";
import { useIsFocused, useNavigation, useRoute } from "@react-navigation/native";
import { useSelector } from "react-redux";
import i18n from "utils/i18n";
import Translate from "utils/translate";
import _ from "lodash";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import ThemedBlock from "components/atoms/ThemedBlock";
import LoadingComponent from "components/atoms/Loading";
import Form, { createTranslationField } from "components/molecules/Form";
import { FormFieldsConstants, FormStatus } from 'utils/constants/form-constants';
import GraphqlBuilder from "utils/request/graphql/builder";
import { tableConstants } from "utils/constants/constants";
import { dataConstants } from "services/redux/data/constants";
import MutateTranslation from 'utils/mutate/translation'
import HelperString from 'utils/helper-string'
import QueryManagementDepartment from 'graphql/queries/management/QueryManagementDepartment'
import MutationManagementDepartment from 'graphql/mutations/management/MutationManagementDepartment'
import HelperManagementRights from "utils/helper-management-rights";
import { managementRightsConstants } from "utils/constants/management-right-constants";
import { AppRoutes } from 'navigations/app-routes'

const ManagementDepartmentScene = () => {
	
	const isFocused = useIsFocused();
	const navigation = useNavigation();
	const route = useRoute();
	
	const _languages = useSelector( state => {
		return state.data[dataConstants.LANGUAGES];
	} )
	
	const [ loading, setLoading ] = useState( true )
	const [ department, setDepartment ] = useState( {} )
	
	const companyGuid = _.get( route.params, 'companyGuid', null );
	const departmentGuid = _.get( route.params, 'departmentGuid', null );
	
	const fetchDepartment = () => {
		if( departmentGuid !== null ) {
			new GraphqlBuilder()
				.addQuery( 'department', QueryManagementDepartment, {
					guid: departmentGuid
				})
				.setResultPath( ['department', 'department'], {} )
				.setOnSuccess( ( result ) => {
					setLoading( false )
					setDepartment( result )
				} )
				.setOnError( setLoading.bind( this, false ) )
				.execute()
			
			return;
		}
		
		setLoading( false )
	}
	
	const saveForm = ( values, { callback } = {} ) => {
		new GraphqlBuilder()
			.addQuery( 'mutation', MutationManagementDepartment, {
				...values,
				company_guid: companyGuid
			})
			.setResultPath( ['mutation', 'department'], {} )
			.setOnStart( () => {
				callback( FormStatus.LOADING )
			})
			.setOnSuccess( ( result ) => {
				MutateTranslation.mutate( tableConstants.DEPARTMENTS, result.guid, values, {
					callbackSuccess: ( t ) => {
						callback( FormStatus.SUCCESS )
						
						navigation.replace( AppRoutes.MANAGEMENT_COMPANY_COMPANIES_DEPARTMENT, {
							departmentGuid: result.guid,
							companyGuid: companyGuid,
						} );
					},
					callbackError: () => {
						callback( FormStatus.ERROR )
					}
				} )
			})
			.setOnError( ( result ) => {
				callback( FormStatus.ERROR )
			})
			.execute()
	}
	
	useEffect( fetchDepartment, [ isFocused, departmentGuid ] )
	
	let content = <LoadingComponent />
	if( !loading ) {
		const formFields = [
			{
				name: 'name',
				label: i18n.t('management:form.name.system_name'),
				type: FormFieldsConstants.TEXT,
			},
			{
				name: 'active',
				label: i18n.t('management:form.active.title'),
				type: FormFieldsConstants.SELECT_ACTIVE
			}
		];
		
		// Add the translations
		if( _.size( _languages ) > 0 ) {
			formFields.push( {
				type: FormFieldsConstants.DIVIDER
			} );
			
			// Add the translation fields
			_.forEach( _languages, ( language ) => {
				const { name, formField } = createTranslationField( 'name', language );
				
				formFields.push( formField );
				
				const translation = Translate.formField( department, 'title', language );
				if( !_.isEmpty( department ) && translation !== null ) {
					department[ name ] = translation;
				}
			});
		}
		
		content = (
			<ThemedBlock
				header={ i18n.t('management:form_block.title_default') }
				shadow
				mediumPadding>
				
				{/* Form */}
				<Form
					key={ HelperString.hash( 'form', department ) }
					values={ department }
					fields={ formFields }
					onSubmit={ saveForm }
					disabled={ !HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.DEPARTMENTS ) }
				/>
			</ThemedBlock>
		)
	}
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>

			<ThemedLayout
				title={ i18n.t('management:pages.company.department.title') }
				topNavigation={ true }
				showBackButton={ true }>
				
				<ThemedBlock transparent />
				
				{ content }
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

export default ManagementDepartmentScene
